import { useMemo, useRef, useState } from 'react';
import {
  Button,
  DatePicker,
  Dropdown,
  FlexContainer,
  InputMask,
  InputText,
  InputTextarea,
  Typography,
} from '@uigovpe/components';
import { Option } from '@/shared/types/certificate-request';
import { AutoSaveState } from '@/shared/hooks/useAutoSave';
import {
  dateToIsoDate,
  dateToTimeString,
  isoDateToDate,
  timeStringToDate,
} from '@/shared/utils/formatting';
import { viaCepApi } from '../../api/viaCepApi';
import { IncidentFormValue, validateIncidentForm } from '../../incidentForm';

const BRAZILIAN_STATES = [
  'AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT','PA','PB',
  'PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO',
].map((code) => ({ label: code, value: code }));

const AUTO_SAVE_LABELS: Record<AutoSaveState, string> = {
  idle: '',
  saving: 'Salvando...',
  saved: 'Rascunho salvo',
  error: 'Não conseguimos salvar o rascunho',
};

interface IncidentStepProps {
  value: IncidentFormValue;
  incidentTypes: Option[];
  autoSaveState: AutoSaveState;
  onChange: (value: IncidentFormValue) => void;
  onNext: () => void;
}

export function IncidentStep({
  value,
  incidentTypes,
  autoSaveState,
  onChange,
  onNext,
}: IncidentStepProps) {
  const [touched, setTouched] = useState(false);
  const [cepStatus, setCepStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  // Espelha `value` a cada render: a resposta do ViaCEP chega depois, e sem
  // isso o merge do endereço usaria o `value` velho de quando a busca
  // começou, apagando o que o cidadão tiver digitado enquanto ela corria.
  const valueRef = useRef(value);
  valueRef.current = value;
  const cepRequestRef = useRef<string | null>(null);

  const typeOptions = useMemo(
    () => incidentTypes.map((type) => ({ label: type.label, value: type.value })),
    [incidentTypes],
  );

  const errors = validateIncidentForm(value);
  const canAdvance = !errors.incidentType && !errors.incidentDate && !errors.incidentTime;

  const change = (field: keyof IncidentFormValue, next: string | null) =>
    onChange({ ...value, [field]: next });

  const errorOf = (field: keyof typeof errors) => (touched ? errors[field] : '');

  /** Ao completar os 8 dígitos do CEP, busca o endereço no ViaCEP e preenche cidade/UF/bairro/rua. */
  const changePostalCode = (raw?: string | null) => {
    const text = raw ?? '';
    change('postalCode', text);

    const digits = text.replace(/\D/g, '');
    cepRequestRef.current = digits;

    if (digits.length !== 8) {
      setCepStatus('idle');
      return;
    }

    setCepStatus('loading');

    viaCepApi
      .lookup(digits)
      .then((address) => {
        if (cepRequestRef.current !== digits) return; // cidadão já mudou o CEP

        if (!address) {
          setCepStatus('error');
          return;
        }

        setCepStatus('idle');
        onChange({
          ...valueRef.current,
          postalCode: text,
          city: address.localidade,
          state: address.uf,
          district: address.bairro,
          street: address.logradouro,
        });
      })
      .catch(() => {
        if (cepRequestRef.current === digits) setCepStatus('error');
      });
  };

  return (
    <div className="cd-step-page">
      <section className="cd-step">
        <div className="cd-step__top">
          <Typography variant="h2" size="lg" fontWeight="bold">
            Sobre a ocorrência
          </Typography>
          <span className="cd-step__autosave" aria-live="polite">
            {AUTO_SAVE_LABELS[autoSaveState]}
          </span>
        </div>

        <Typography variant="p" size="sm" className="cd-step__hint">
          A certidão é emitida pelo Corpo de Bombeiros Militar de Pernambuco. Quanto
          mais detalhes você der, mais rápido a equipe localiza o atendimento.
        </Typography>

        <FlexContainer direction="col" gap="6">
          <Dropdown
            inputId="incident-type"
            label="Natureza do fato*"
            placeholder="Selecione"
            options={typeOptions}
            value={value.incidentType ?? null}
            onChange={(e) => change('incidentType', e.value)}
            className={errorOf('incidentType') ? 'p-invalid' : ''}
            suportText={errorOf('incidentType')}
          />

          <div className="cd-grid cd-grid--2">
            <DatePicker
              inputId="incident-date"
              label="Data da ocorrência*"
              placeholder="DD/MM/AAAA"
              value={isoDateToDate(value.incidentDate)}
              maxDate={new Date()}
              onChange={(e) => change('incidentDate', dateToIsoDate(e.value))}
              invalid={Boolean(errorOf('incidentDate'))}
              supportText={errorOf('incidentDate') || 'Aproximada já ajuda.'}
            />
            <DatePicker
              inputId="incident-time"
              label="Horário aproximado*"
              timeOnly
              placeholder="HH:MM"
              value={timeStringToDate(value.incidentTime)}
              onChange={(e) => change('incidentTime', dateToTimeString(e.value))}
              invalid={Boolean(errorOf('incidentTime'))}
              supportText={errorOf('incidentTime')}
            />
          </div>

          <Typography variant="h3" size="default" fontWeight="medium">
            Onde aconteceu
          </Typography>

          <div className="cd-grid cd-grid--3">
            <InputMask
              id="incident-postal-code"
              label="CEP"
              mask="99999-999"
              placeholder="00000-000"
              value={value.postalCode}
              onChange={(e) => changePostalCode(e.value)}
              suportText={
                cepStatus === 'loading'
                  ? 'Buscando endereço...'
                  : cepStatus === 'error'
                    ? 'CEP não encontrado. Preencha o endereço manualmente.'
                    : undefined
              }
            />
            <InputText
              inputId="incident-city"
              label="Cidade"
              value={value.city}
              onChange={(e) => change('city', e.target.value)}
            />
            <Dropdown
              inputId="incident-state"
              label="UF"
              placeholder="UF"
              options={BRAZILIAN_STATES}
              value={value.state || null}
              onChange={(e) => change('state', e.value ?? '')}
            />
          </div>

          <div className="cd-grid cd-grid--2">
            <InputText
              inputId="incident-district"
              label="Bairro"
              value={value.district}
              onChange={(e) => change('district', e.target.value)}
            />
            <InputText
              inputId="incident-street"
              label="Rua ou avenida"
              value={value.street}
              onChange={(e) => change('street', e.target.value)}
            />
          </div>

          <div className="cd-grid cd-grid--2">
            <InputText
              inputId="incident-number"
              label="Número"
              value={value.number}
              onChange={(e) => change('number', e.target.value)}
            />
            <InputText
              inputId="incident-complement"
              label="Complemento"
              value={value.complement}
              onChange={(e) => change('complement', e.target.value)}
            />
          </div>

          <InputTextarea
            inputId="incident-description"
            label="O que aconteceu"
            rows={4}
            maxLength={500}
            placeholder="Descreva a ocorrência com suas palavras."
            value={value.description}
            onChange={(e) => change('description', e.target.value)}
            suportText={`${value.description.length}/500`}
          />
        </FlexContainer>
      </section>

      <div className="cd-actions cd-actions--outside">
        <Button
          label="Continuar"
          icon="pi pi-arrow-right"
          iconPos="right"
          onClick={() => {
            setTouched(true);
            if (canAdvance) onNext();
          }}
        />
      </div>
    </div>
  );
}
